import {getWindow} from "./WindowWrapper";
import {normalizeLocale} from "../localization/LocaleSupport";
import * as queryString from "query-string";
import {Listener, ListenerList} from '../listeners/Listeners';
import {LocaleMap} from '../localization';

/**
 * Functions for initializing, setting and getting environment, and helper functions for locales.
 */

export enum DeviceType {
   DESKTOP = 'DESKTOP',
   PHONE = 'PHONE',
   TABLET = 'TABLET'
}

export enum NativeAppType {
   ANDROID = 'ANDROID',
   DESKTOP = 'DESKTOP',
   IOS = 'IOS',
   NONE = 'NONE'
}

export enum BrowserOS {
   IOS = 'IOS',
   ANDROID = 'ANDROID',
   WINDOWS = 'WINDOWS',
   MAC = 'MAC',
   UNKNOWN = 'UNKNOWN'
}

export enum BrowserType {
   SAFARI = 'SAFARI',
   CHROME = 'CHROME',
   FIREFOX = 'FIREFOX',
   IE = 'IE',
   EDGE = 'EDGE',
   UNKNOWN = 'UNKNOWN'
}

/**
 * Main properties for UniFocus applications.
 */
export interface UniFocusEnvironment {
   readonly ssoPageUrl: string;
   readonly allowedDomains: string[];
   readonly portalBaseUrl: string;
   readonly portalHomePageUrl: string;
   readonly portalMenuUrl: string;
   readonly loginPageUrl: string;
   readonly errorReportUrl?: string;
   readonly singularityEventsUrl: string;
   readonly deviceType: DeviceType;
   readonly nativeAppType: NativeAppType;
   readonly browserOS: BrowserOS;
   readonly browserType: BrowserType;
   readonly mobile: boolean;
   readonly locale: string;
   readonly timeMode24Hour: boolean;
   readonly partnerCode?: string;
   readonly databaseId?: number;
   readonly propertyId?: number;
   readonly buildNo?: string;
   readonly buildVersion?: string;
   readonly appStoreUrl?: string;
   readonly appStoreReviewUrl?: string;
   readonly nativeAppVersion?: string;
   readonly applicationId: string;
   readonly applicationName: string;
   readonly preferencesApiUrl?: string;
   readonly enabledFeatureToggles?: string[];
   readonly resourceOverrides?: LocaleMap;
}

const initializeApplicationListeners = new ListenerList<UniFocusEnvironment>();

export const addInitializeApplicationListener = (listener: Listener<UniFocusEnvironment>) => {
   initializeApplicationListeners.addListener(listener);
};

const handleApplicationFailedToLoad = () => {
   const unifocusLoadingDiv = document.getElementById("unifocus-loading-div");

   if (unifocusLoadingDiv && unifocusLoadingDiv.parentNode) {
      unifocusLoadingDiv.parentNode.removeChild(unifocusLoadingDiv);
   }

   const divNode: HTMLDivElement = document.createElement('div');
   divNode.setAttribute('style', 'margin: auto; width: 50%; padding: 10px; line-height: 200px; height: 200px; text-align: center;');
   divNode.innerHTML = '<h3>The Application Failed To Load</h3>';

   const rootDiv = document.getElementById('root');

   if (rootDiv) {
      rootDiv.appendChild(divNode);
   }
};

const initializeEnvironmentCallback = (applicationId: string, onReady: (environment: UniFocusEnvironment) => void) => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus && unifocus.initializeEnvironment) {
      if (applicationId !== undefined && unifocus.environment.applicationId === undefined) {
         addPropertyToEnvironment('applicationId', applicationId); // used for backwards compatibility
      }

      unifocus.initializeEnvironment((environment: UniFocusEnvironment) => {
         if (environment.applicationName) {
            anyWindow.document.title = environment.applicationName;
         }

         initializeApplicationListeners.notifyListeners(environment);
         onReady(environment);
      });
   }
   else {
      console.error("*********    The unifocus application environment script did not initialize the environment?   ***********");
      handleApplicationFailedToLoad();
   }
};

/**
 * Call this function to initialize a test environment. Function adds properties to
 * unifocus object on global window variable and executes passed callback function
 * with unifocus environment as argument.
 *
 * @param {string} applicationId
 * @param {(environment: UniFocusEnvironment) => void} onReady
 */
export const initializeTestEnvironment = (applicationId: string, onReady: (environment: UniFocusEnvironment) => void) => {
   const anyWindow = getWindow() as any;
   const defaultLocale = getDefaultLocale();
   const defaultTimeMode24Hour = getDefaultTimeMode24Hour(defaultLocale);

   setDefaultEnvironment(defaultLocale, defaultTimeMode24Hour, applicationId);
   initializeApplicationListeners.notifyListeners(anyWindow.unifocus.environment);
   onReady(anyWindow.unifocus.environment);
};

/**
 * Call this function to initialize environment. Function adds properties to
 * unifocus object on global window variable and executes passed callback function
 * with unifocus environment as argument.
 *
 * @param {string} applicationId
 * @param {(environment: UniFocusEnvironment) => void} onReady
 */
export const initializeEnvironment = (applicationId: string, onReady: (environment: UniFocusEnvironment) => void) => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus && unifocus.initializeEnvironment) {
      console.error("The legacy unifocus environment has been detected, this is due to having the legacy application-environment script in your index.html (see migration guide). This environment will be discarded and the modern environment will be created.");
      delete anyWindow.unifocus;
   }

   const script = anyWindow.document.createElement('script');
   script.type = "application/javascript";
   script.onload = function() { initializeEnvironmentCallback(applicationId, onReady) };
   script.onerror = function () {
      console.error("*********    Unable to load the unifocus application environment script!   ***********");
      handleApplicationFailedToLoad();
   };
   script.src = '/singularity-support/application-environment/index.js?applicationId=' + applicationId + '&' + new Date().getTime();
   anyWindow.document.getElementsByTagName('head')[0].appendChild(script);
};

const addPropertyToEnvironment = (property: string, value: any): void => {
   const environment = getEnvironment() as any;

   if (!environment[property]) {
      environment[property] = value;
   }
};

/**
 * Call this function to get environment properties.
 *
 * @return Returns unifocus environment or locale and timeMode24Hour properties.
 */
export const getEnvironment = (): UniFocusEnvironment => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus) {
      return unifocus.environment;
   }

   const defaultLocale = getDefaultLocale();
   const defaultTimeMode24Hour = getDefaultTimeMode24Hour(defaultLocale);

   return {
      locale: defaultLocale,
      timeMode24Hour: defaultTimeMode24Hour,
   } as UniFocusEnvironment;
};

/**
 * Call this function to set default environment properties.
 * Properties are set on window variable as unifocus object containing locale, timeMode24Hour and applicationId properties.
 */
export const setDefaultEnvironment = (locale: string, timeMode24Hour: boolean, applicationId: string): void => {
   (getWindow() as any).unifocus = {
      environment: {
         locale: locale,
         timeMode24Hour: timeMode24Hour,
         applicationId: applicationId
      }
   };
};

export const isOriginAllowed = (origin: string): boolean => {
   const allowedDomains = getAllowedDomains();

   if (!allowedDomains || allowedDomains.length === 0) {
      return true;
   }

   if (!origin || origin.trim().length === 0) {
      return false;
   }

   const requestDomain = getDomain(origin);

   if (requestDomain === 'localhost') {
      return true;
   }

   for (let i = 0; i < allowedDomains.length; i++) {
      if (allowedDomains[i] === requestDomain) {
         return true;
      }
   }

   return false;
};

const getDomain = (url: string): string => {
   const origin = new URL(url).hostname.toLowerCase();
   const lastDot = origin.lastIndexOf('.');
   const secondDot = origin.lastIndexOf('.', lastDot - 1);

   if (lastDot < 0 || secondDot < 0) {
      return origin;
   }

   return origin.substring(secondDot + 1);
};

const getAllowedDomains = () => {
   const environment = getEnvironment();

   if (Array.isArray(environment.allowedDomains)) {
      return environment.allowedDomains;
   }

   return null;
};

export const switchDatabaseAndProperty = (databaseId: number, propertyId: number, saveAsDefault: boolean): void => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus) {
      unifocus.switchDatabaseAndProperty(databaseId, propertyId, saveAsDefault);
   }
   else {
      throw new Error('Unable to switchDatabaseAndProperty due to missing unifocus object');
   }
};

export const refreshLocale = (): void => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus) {
      unifocus.refreshLocale();
   }
   else {
      throw new Error('Unable to refreshLocale due to missing unifocus object');
   }
};

export const refreshAuthorizationToken = () => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus) {
      unifocus.sendRefreshAuthorizationTokenEvent();
   }
};

export const signOut = (): void => {
   const anyWindow = getWindow() as any;
   const unifocus = anyWindow.unifocus;

   if (unifocus) {
      unifocus.signOut();
   }
   else {
      throw new Error('Unable to signOut due to missing unifocus object');
   }
};

export const isFeatureToggleEnabled = (toggleName: string): boolean => {
   const enabledFeatureToggles = getEnvironment().enabledFeatureToggles;

   if (enabledFeatureToggles) {
      return enabledFeatureToggles.includes(toggleName);
   }

   return false;
};

const getDefaultLocale = () => {
   let locale = getLocaleFromQueryParameter();

   if (locale) {
      return locale;
   }

   locale = getBrowserLocale();

   if (locale) {
      return locale;
   }

   return 'en-US';
};

const getDefaultTimeMode24Hour = (locale: string): boolean => {
   return locale.split('-')[1] !== 'US';
};

const getLocaleFromQueryParameter = () => {
   const localeQueryValue = getWindow().location ? queryString.parse(getWindow().location.search)['locale'] : null;
   const locale = Array.isArray(localeQueryValue) ? localeQueryValue[0] : localeQueryValue;

   if (locale) {
      return normalizeLocale(locale);
   }

   return null;
};

const getBrowserLocale = (): string | null => {
   const browserLanguage = getWindow().navigator ? getWindow().navigator.language : null;

   if (browserLanguage) {
      return normalizeLocale(browserLanguage);
   }

   return null;
};
